import { SearchOutlined } from "@mui/icons-material";
import ReplyIcon from "@mui/icons-material/Reply";
import {
	Box,
	Button,
	Chip,
	FormControlLabel,
	InputBase,
	Pagination,
	Paper,
	Switch,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listAllMessages, listAllUnresolvedAndUnreadThreads } from "../../actions/util";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ESRState, Thread } from "../../types/data";

export const MessagesList = () => {
	const [user, setUser] = useCurrentUser();
	const [searchString, setSearchString] = useState("");
	const [page, setPage] = useState(1);
	const totalPages = useSelector(
		(state: ESRState) => state.allmessageTotalPages,
	);
	const [showUnreadOnly, setShowUnreadOnly] = useState(false);
	const navigate = useNavigate();
	function onNavigateToESR(thread: Thread) {
		const queryParam = `?thread_id=${thread.id}`;
		const esrId = thread.addendum_id || thread.esr_id;
		const activeLabel = thread.addendum_label || "initial";
		const activeId = thread.esr_id;
		if (thread?.question_id) {
			navigate(
				`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${thread.question_id}${queryParam}`,
				{ replace: true },
			);
		} else {
			navigate(`/esr/${esrId}/${activeLabel}/${activeId}/0${queryParam}`, {
				replace: true,
			});
		}
	}

	useEffect(() => {
		if (user) {
			dispatch(listAllMessages(page, searchString, showUnreadOnly));
		}
	}, [user, page, searchString, showUnreadOnly]);
	useEffect(() => {
		setPage(1);
	}, [searchString, showUnreadOnly]);
	useEffect(()=>{
		if(user) dispatch(listAllUnresolvedAndUnreadThreads());
	}, [user]);

	const dispatch = useDispatch();

	const allThreads = useSelector((state: ESRState) => state.allThreads);
	const filteredThreads = useMemo(() => {
		const query = searchString.trim();

		return Object.values(allThreads || {}).filter((thread) => {
			if (!query) return true;

			const esrId = String(thread.esr_id);

			const addendum = String(
				`${thread.addendum_id || ""}${thread.addendum_label || ""}`,
			);

			return esrId.includes(query) || addendum.includes(query);
		});
	}, [allThreads, searchString]);

	const sortedThreads = useMemo(() => {
		return filteredThreads.sort(
			(a, b) =>
				new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
		);
	}, [filteredThreads]);
	useEffect(() => {
		if (
			selectedThread &&
			!sortedThreads.some((t) => t.id === selectedThread.id)
		) {
			setSelectedThread(sortedThreads[0] || null);
		}
	}, [sortedThreads]);
	const getGroupUnreadCount = (threads: Thread[]) =>
		threads.reduce((sum, thread) => sum + (thread.unread_messages || 0), 0);
	const getThreadUnreadCount = (thread: Thread) => thread.unread_messages || 0;
	const timerRef = useRef(-1);

	const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
	const unreadMessageCount = useSelector(
		(state: ESRState) => state.unread_message_count,
	);
	return (
		<Box
			sx={{
				p: 4,
				height: "100%",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Header */}
			<Box sx={{ display: "flex", mb: 3 }}>
				<Typography
					component="h1"
					sx={{
						fontSize: 24,
						fontWeight: 700,
					}}
				>
					All Messages
				</Typography>
				<Box sx={{ flex: 1 }} />
				<FormControlLabel
					control={
						<Switch
							checked={showUnreadOnly}
							onChange={(e) => setShowUnreadOnly(e.target.checked)}
							color="primary"
						/>
					}
					label={`View Unread Messages (${unreadMessageCount})`}
				/>

				<Box sx={{ flex: 1 }} />

				<Paper
					square
					elevation={0}
					sx={{
						p: "2px 4px",
						display: "flex",
						alignItems: "center",
						width: 400,
						bgcolor: "#0000000A",
					}}
				>
					<InputBase
						sx={{
							ml: 2,
							flex: 1,
							"& input::placeholder": {
								color: "#4a4a4a",
								opacity: 1,
							},
						}}
						value={searchString}
						onChange={(e) => setSearchString(e.target.value)}
						placeholder="Search by Seq. #"
					/>
					<SearchOutlined />
				</Paper>
			</Box>

			{/* 2-column layout */}
			<Box
				sx={{
					flex: 1,
					display: "flex",
					border: "1px solid #ddd",
					borderRadius: 1,
					overflow: "hidden",
					minHeight: 0,
				}}
			>
				<Box
					sx={{
						width: 450,
						overflowY: "auto",
						borderRight: "1px solid #ddd",
					}}
				>
					{sortedThreads.map((thread: Thread) => (
						<Box
							key={thread.id}
							onClick={() => setSelectedThread(thread)}
							sx={{
								p: 2,
								cursor: "pointer",
								borderBottom: "1px solid #eee",
								bgcolor: selectedThread?.id === thread.id ? "#f3f8fc" : "white",
								margin: "2%",
								border:
									selectedThread?.id === thread.id
										? "1px solid #1976d2"
										: "1px solid transparent",
								boxShadow:
									selectedThread?.id === thread.id
										? "0 6px 18px rgba(0,0,0,0.18)"
										: "none",
								transform:
									selectedThread?.id === thread.id
										? "translateY(-1px)"
										: "none",
								transition: "all 0.2s ease",
								position: "relative",
								zIndex: selectedThread?.id === thread.id ? 1 : 0,

								"&:hover": {
									bgcolor: "#f5f5f5",
									boxShadow:
										selectedThread?.id === thread.id
											? "0 8px 20px rgba(0,0,0,0.22)"
											: "0 2px 8px rgba(0,0,0,0.08)",
								},
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								<Typography variant="body2" fontWeight={600}>
									{thread.addendum_id?.length > 0 &&
									thread.addendum_label?.length > 0
										? `ESR #${thread.addendum_id}${thread.addendum_label}`
										: `ESR #${thread.esr_id}`}
								</Typography>

								{thread.unread_messages > 0 && (
									<Chip
										size="small"
										color="error"
										label={thread.unread_messages}
										sx={{
											height: 20,
											"& .MuiChip-label": {
												px: 1,
												fontSize: 11,
											},
										}}
									/>
								)}
							</Box>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
								}}
							>
								{thread.subject}
							</Typography>

							<Typography variant="caption" color="text.secondary">
								{thread.creator_id} •{" "}
								{new Date(thread.timestamp).toLocaleString()}
							</Typography>
						</Box>
					))}
				</Box>

				{/* =====================================================
                 CONVERSATION
            ====================================================== */}

				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						p: 3,
						bgcolor: "#fafafa",
					}}
				>
					{!selectedThread ? (
						<Typography color="text.secondary">
							Select a thread to view messages
						</Typography>
					) : (
						<>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 3,
								}}
							>
								<Typography 
								variant="h5"
								sx={{
										 color: "success.main",
										fontWeight: 700,
									}}>
									{selectedThread.addendum_id?.length > 0 &&
									selectedThread.addendum_label?.length > 0
										? `ESR #${selectedThread.addendum_id}${selectedThread.addendum_label}`
										: `ESR #${selectedThread.esr_id}`}  
								</Typography>
								<Typography
									variant="h5"
									sx={{
										fontWeight: 700,
										flex: 1,
									}}
								>
									{selectedThread.subject}
								</Typography>

								<Button
									variant="contained"
									size="small"
									startIcon={<ReplyIcon />}
									onClick={() => onNavigateToESR(selectedThread)}
								>
									Go To Thread
								</Button>
							</Box>

							{selectedThread.messages
								?.slice()
								.reverse()
								.map((message) => (
									<Box
										key={message.id}
										sx={{
											mb: 2,
											p: 2,
											bgcolor: "white",
											border: "1px solid #ddd",
											borderRadius: 1,
										}}
									>
										<Typography fontWeight={600}>
											{message.sender_id}
										</Typography>

										<Typography variant="caption" color="text.secondary">
											{new Date(message.timestamp).toLocaleString()}
										</Typography>

										<Typography
											sx={{
												mt: 1,
											}}
										>
											{message.contents}
										</Typography>

										{message.file_ids?.length > 0 && (
											<Typography
												variant="caption"
												sx={{
													display: "block",
													mt: 1,
												}}
											>
												📎 {message.file_ids.length} attachment(s)
											</Typography>
										)}
									</Box>
								))}
						</>
					)}
				</Box>
			</Box>
			{/* Pagenation bar */}
			<Pagination
				page={page}
				count={totalPages}
				color="primary"
				onChange={(_, value) => {
					setPage(value);
				}}
			/>
		</Box>
	);
};
